import React, { useEffect, useRef, useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import mermaid from "mermaid";

import { InfraType, StackTemplate, TemplateDeploymentMode } from "../../types";

import {
  COMPLEXITY_COLOR,
  COMPLEXITY_LABEL,
  LANGUAGE_LABEL,
  TEMPLATE_MODE_LABEL,
} from "./template-gallery-constants";
import {
  getDiagramForTemplate,
  stackConfigTools,
} from "./template-gallery-helpers";

export function TemplateSkeleton() {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
        gap: 2,
      }}
    >
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} variant="rounded" height={160} />
      ))}
    </Box>
  );
}

interface TemplateListItemProps {
  template: StackTemplate;
  infraType: InfraType;
  selected: boolean;
  onSelect: (template: StackTemplate) => void;
  onExpand: (template: StackTemplate) => void;
  expanded: boolean;
}

export function TemplateListItem({
  template,
  infraType,
  selected,
  onSelect,
  onExpand,
  expanded,
}: TemplateListItemProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [diagramSvg, setDiagramSvg] = useState<string>("");
  const tools = stackConfigTools(template.stackConfig);

  // Generate diagram when expanded - use registry or fall back to dynamic
  useEffect(() => {
    if (!expanded || !containerRef.current) return;

    const renderDiagram = async () => {
      try {
        // Try to get pre-built diagram from registry
        const diagramConfig = getDiagramForTemplate(template.slug, infraType);

        let diagram: string;
        if (diagramConfig) {
          diagram = diagramConfig.diagram;
        } else {
          // Fall back to generating from services
          const services = template.services || [];

          if (services.length > 0) {
            const nodes: string[] = [];
            const connections: string[] = [];

            nodes.push('    Client["Client Browser"]');

            services.forEach((svc) => {
              const typeLabel =
                svc.type === "stateful"
                  ? "DB"
                  : svc.type === "static"
                    ? "UI"
                    : "API";
              nodes.push(`    ${svc.name}["${svc.name}<br/>${typeLabel}"]`);
            });

            const publicServices = services.filter(
              (s: { internetFacing: boolean }) => s.internetFacing,
            );
            if (publicServices.length > 0) {
              connections.push('    Client --> LB["Load Balancer"]');
              publicServices.forEach((s: { name: string }) => {
                connections.push(`    LB --> ${s.name}`);
              });
            } else {
              connections.push("    Client --> App");
            }

            const databases = services.filter(
              (s: { type: string }) => s.type === "stateful",
            );
            const servers = services.filter(
              (s: { type: string }) =>
                s.type === "server" || s.type === "static",
            );
            databases.forEach((db: { name: string }) => {
              servers.forEach((srv: { name: string }) => {
                if (srv.name !== db.name) {
                  connections.push(`    ${srv.name} --> ${db.name}`);
                }
              });
            });

            diagram = `flowchart TB
${nodes.join("\n")}

${connections.join("\n")}

    style Client fill:#374151,stroke:#374151,color:#fff
    style LB fill:#0066ff,stroke:#0066ff,color:#fff`;
          } else {
            diagram = `flowchart TB
    Client[Client] --> App[${template.name}]
    style Client fill:#374151,stroke:#374151,color:#fff
    style App fill:#0066ff,stroke:#0066ff,color:#fff`;
          }
        }

        const id = `template-diagram-${template.slug}-${Date.now()}`;
        const { svg } = await mermaid.render(id, diagram);
        setDiagramSvg(svg);
      } catch {
        // Diagram rendering failed silently
      }
    };

    void renderDiagram();
  }, [expanded, template, infraType]);

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 1,
        overflow: "hidden",
        borderColor: selected ? "primary.main" : "divider",
        borderWidth: selected ? 2 : 1,
      }}
    >
      {/* Main row */}
      <Box
        role="button"
        tabIndex={0}
        aria-label={`Select ${template.name} template`}
        onClick={() => onSelect(template)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect(template);
          }
        }}
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          cursor: "pointer",
          bgcolor: selected ? "primary.lighter" : "background.paper",
          "&:hover": {
            bgcolor: selected ? "primary.lighter" : "action.hover",
          },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: -2,
          },
        }}
      >
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onExpand(template);
          }}
          sx={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        >
          <ExpandMoreIcon />
        </IconButton>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {template.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {template.description}
          </Typography>
        </Box>

        {template.language && (
          <Chip
            label={LANGUAGE_LABEL[template.language]}
            size="small"
            sx={{ height: 20, fontSize: "0.6rem" }}
          />
        )}

        <Chip
          label={COMPLEXITY_LABEL[template.complexity]}
          color={COMPLEXITY_COLOR[template.complexity]}
          size="small"
          sx={{ height: 20, fontSize: "0.65rem" }}
        />

        {template.tags.includes("recommended") && (
          <Chip
            label="Recommended"
            color="primary"
            size="small"
            sx={{ height: 20, fontSize: "0.6rem" }}
          />
        )}
      </Box>

      {/* Expanded details */}
      <Collapse in={expanded}>
        <Box
          sx={{
            p: 2,
            pt: 0,
            bgcolor: "grey.50",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}>
            {/* Architecture diagram — full width for maximum visibility */}
            <Box
              ref={containerRef}
              sx={{
                width: "100%",
                minHeight: 280,
                bgcolor: "white",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid",
                borderColor: "divider",
                overflow: "auto",
                p: 2,
                "& svg": { width: "100%", height: "auto" },
              }}
            >
              {diagramSvg ? (
                <Box dangerouslySetInnerHTML={{ __html: diagramSvg }} />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Loading diagram...
                </Typography>
              )}
            </Box>

            {/* Template details */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Tech Stack
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 2 }}>
                {Array.from(new Set(tools)).map((tool) => (
                  <Chip
                    key={tool}
                    label={tool}
                    size="small"
                    variant="outlined"
                    sx={{ height: 22, fontSize: "0.7rem" }}
                  />
                ))}
              </Stack>

              {template.deploymentModes &&
                template.deploymentModes.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Deployment Modes
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {(
                        template.deploymentModes as TemplateDeploymentMode[]
                      ).map((mode) => (
                        <Chip
                          key={mode}
                          label={TEMPLATE_MODE_LABEL[mode] ?? mode}
                          size="small"
                          sx={{ height: 22, fontSize: "0.7rem" }}
                        />
                      ))}
                    </Stack>
                  </>
                )}
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
}
